/**
 * The API error envelope, translated once (docs/07-implementation-roadmap.md F0.6).
 *
 * Every failure the API can produce arrives as `{ error: { code, message,
 * details } }` (docs/04 §8). This module turns that into an `AppError` carrying
 * the one thing each caller actually needs:
 *
 *   400 / 422  → field errors, so a rejected form marks the field that caused it
 *   401        → the refresh path (F1.3 hooks this; F0 only classifies it)
 *   403        → a permission/region message, never a generic toast (F1.10)
 *   409        → a conflict message
 *   429        → how long to wait, and which budget was spent (F7.5)
 *   5xx        → the error boundary, showing the correlation ID as a support code
 *
 * Messages are Bahasa Indonesia and live here rather than in components,
 * because these are the strings for failures no screen is specific about. A
 * screen with something better to say uses `error.code` and says it.
 */

export type AppErrorKind =
  | 'validation'
  | 'unauthenticated'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'businessRule'
  | 'rateLimited'
  | 'server'
  | 'network';

/** The envelope in docs/04 §8. */
interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
}

const KIND_BY_STATUS: Record<number, AppErrorKind> = {
  400: 'validation',
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  422: 'businessRule',
  429: 'rateLimited',
};

const MESSAGE_BY_KIND: Record<AppErrorKind, string> = {
  validation: 'Ada isian yang belum benar. Periksa kembali kolom yang ditandai.',
  unauthenticated: 'Sesi Anda telah berakhir. Silakan masuk kembali.',
  forbidden: 'Anda tidak memiliki akses ke data ini.',
  notFound: 'Data yang Anda cari tidak ditemukan.',
  conflict: 'Data ini bentrok dengan data yang sudah ada.',
  businessRule: 'Perubahan ini tidak dapat diproses.',
  rateLimited: 'Terlalu banyak permintaan. Coba lagi beberapa saat lagi.',
  server: 'Terjadi gangguan pada sistem. Sampaikan kode dukungan di bawah ini kepada tim teknis.',
  network: 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda, lalu coba lagi.',
};

/** Errors a user can act on by trying again; anything else needs a person. */
const RETRYABLE: ReadonlySet<AppErrorKind> = new Set<AppErrorKind>(['network', 'rateLimited', 'server']);

/**
 * Failures where the API **did not answer the question** — it was busy, broken,
 * or not reached at all — as opposed to answering it unfavourably.
 *
 * The same three kinds as `RETRYABLE` today, and a separate set on purpose:
 * they answer different questions ("may this person press a button?" versus
 * "does this failure decide anything?") and one can move without the other. A
 * fourth retryable kind added later must not silently change who stays signed
 * in.
 */
const TRANSIENT: ReadonlySet<AppErrorKind> = new Set<AppErrorKind>(['network', 'rateLimited', 'server']);

/**
 * Which budget a 429 spent (F7.5). The two are not the same event and must not
 * read the same way.
 *
 * - `shared` — the blanket `API_RATE_LIMIT_MAX`, 100 requests per minute **per
 *   IP**. The church office is behind one public address, so this is very
 *   nearly always somebody *else's* traffic: a colleague running a bulk update
 *   in the next room spends the budget the person reading the message is
 *   refused by. Copy that says "you made too many requests" would be false and
 *   would send them looking for something they did wrong.
 * - `export` — `EXPORT_RATE_LIMIT_MAX`, five per hour **per user**, keyed on the
 *   token's subject (`backend/src/common/throttling/export-rate-limit.ts`).
 *   That one really is personal, and an hour is long enough that saying so
 *   matters.
 *
 * They are told apart by which header carried the wait, which is a property of
 * `@nestjs/throttler`: a named throttler suffixes its headers, so the export
 * budget answers `Retry-After-export` and the blanket one answers `Retry-After`.
 */
export type RateLimitScope = 'shared' | 'export';

const EXPORT_RETRY_AFTER_HEADER = 'retry-after-export';

/**
 * `Retry-After`, in seconds.
 *
 * The API sends delta-seconds (`ThrottlerGuard` writes `timeToBlockExpire`,
 * already ceiled to seconds). The HTTP-date form is accepted too because the
 * spec permits it and a proxy is entitled to rewrite one into the other — and a
 * countdown is the one thing on this screen a person is going to trust.
 *
 * Zero and negative are dropped: a window that has already rolled is not a wait.
 */
function parseRetryAfter(raw: string | null): number | undefined {
  if (!raw) return undefined;

  const seconds = Number(raw.trim());
  if (Number.isFinite(seconds)) return seconds > 0 ? Math.ceil(seconds) : undefined;

  const until = Date.parse(raw);
  if (Number.isNaN(until)) return undefined;
  const remaining = Math.ceil((until - Date.now()) / 1000);
  return remaining > 0 ? remaining : undefined;
}

function readRateLimit(response: Response): { retryAfterSeconds?: number; rateLimitScope?: RateLimitScope } {
  const shared = parseRetryAfter(response.headers.get('Retry-After'));
  if (shared !== undefined) return { retryAfterSeconds: shared, rateLimitScope: 'shared' };

  const exported = parseRetryAfter(response.headers.get(EXPORT_RETRY_AFTER_HEADER));
  if (exported !== undefined) return { retryAfterSeconds: exported, rateLimitScope: 'export' };

  // Blocked, but no header identified which budget — stripped in transit, or
  // the window rolled between the block and the write. **The scope stays
  // unknown rather than defaulting to the likelier one.** Guessing "shared"
  // would tell someone refused by their own five-per-hour export budget that
  // their colleagues caused it and that a minute's wait will clear it; both
  // are false and the second sends them back to press the button again.
  return { rateLimitScope: response.headers.has(EXPORT_RETRY_AFTER_HEADER) ? 'export' : undefined };
}

/** "45 detik", "2 menit" — the unit a person would use for that length of wait. */
export function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds} detik`;
  return `${Math.ceil(seconds / 60)} menit`;
}

function rateLimitMessage(scope: RateLimitScope | undefined, retryAfterSeconds: number | undefined): string {
  const wait = retryAfterSeconds === undefined ? '' : ` Coba lagi dalam ${formatWait(retryAfterSeconds)}.`;

  switch (scope) {
    case 'export':
      return `Ekspor dibatasi beberapa berkas per jam untuk setiap pengguna, dan batas Anda sudah terpakai.${wait}`;
    case 'shared':
      return `Sistem sedang menerima terlalu banyak permintaan dari jaringan kantor — batasnya dihitung untuk seluruh kantor, bukan per orang, jadi ini bisa terjadi karena pekerjaan rekan Anda.${wait}`;
    default:
      // Which budget is unknown, so the message says only what is certain.
      return MESSAGE_BY_KIND.rateLimited;
  }
}

/**
 * Kinds whose server message is *written for the person reading it* and is more
 * useful than anything this module could say generically — a 422 from docs/04
 * §2.1 names the business rule that was broken, and a 403 names the region
 * boundary that was crossed.
 *
 * Everything else gets the copy above instead. The server's text for those is
 * framework output, not prose: an unrouted path answers "Cannot GET
 * /api/v1/...", which tells a staff member nothing and leaks a route shape for
 * no benefit.
 */
const RELAY_SERVER_MESSAGE: ReadonlySet<AppErrorKind> = new Set<AppErrorKind>([
  'validation',
  'businessRule',
  'conflict',
  'forbidden',
]);

export class AppError extends Error {
  readonly kind: AppErrorKind;
  /** The API's own `error.code`, e.g. `VALIDATION_ERROR`. Absent on a network failure. */
  readonly code?: string;
  readonly status?: number;
  /** Ready to show a user, in Bahasa Indonesia. `message` stays the raw server text for logs. */
  readonly userMessage: string;
  /** Field name → messages, for 400/422. Empty when the server named no field. */
  readonly fieldErrors: Record<string, string[]>;
  readonly details?: unknown;
  /** Ties this failure to its server-side log trail (docs/90 §2.5). */
  readonly correlationId?: string;
  /** How long the server said to wait, on a 429 that carried `Retry-After`. */
  readonly retryAfterSeconds?: number;
  /**
   * When the wait is over, as epoch milliseconds — fixed at the moment the
   * response arrived rather than at the moment something renders it.
   *
   * Which is the difference between a countdown and a stopwatch: an error is
   * cached and re-rendered, and a screen that recomputed "now + 60s" on mount
   * would restart the wait every time the person navigated back to it, showing
   * a minute left forever.
   */
  readonly retryAt?: number;
  /** Which budget a 429 spent. Absent on every other kind. */
  readonly rateLimitScope?: RateLimitScope;

  constructor(init: {
    kind: AppErrorKind;
    message: string;
    userMessage: string;
    code?: string;
    status?: number;
    fieldErrors?: Record<string, string[]>;
    details?: unknown;
    correlationId?: string;
    retryAfterSeconds?: number;
    retryAt?: number;
    rateLimitScope?: RateLimitScope;
    cause?: unknown;
  }) {
    super(init.message, { cause: init.cause });
    this.name = 'AppError';
    this.kind = init.kind;
    this.code = init.code;
    this.status = init.status;
    this.userMessage = init.userMessage;
    this.fieldErrors = init.fieldErrors ?? {};
    this.details = init.details;
    this.correlationId = init.correlationId;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.retryAt = init.retryAt;
    this.rateLimitScope = init.rateLimitScope;
  }

  /** Trying again could work — so a screen offers the person a "coba lagi". */
  get isRetryable(): boolean {
    return RETRYABLE.has(this.kind);
  }

  /**
   * Worth a *silent* second attempt, which is a narrower question than
   * `isRetryable` (F7.5).
   *
   * A 429 is retryable by a person and must not be retried by the client. The
   * budget is 100 requests per minute for the whole office and the window is up
   * to a minute long, so an automatic retry a second later is refused for the
   * same reason, arrives before anything on screen has changed, and spends one
   * more of the requests the office is short of. The wait belongs to the person,
   * who can be shown how long it is.
   */
  get isAutoRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'server';
  }

  /**
   * Whether this failure means the session is over — for the one caller that
   * asks, `refresh.ts`.
   *
   * **Stated as "everything except the transient kinds" rather than as a list
   * of the statuses that end a session**, which is the narrower and more
   * tempting spelling and is wrong. `POST /auth/refresh` with no cookie at all
   * — a first visit, a cleared browser, a `SameSite` drop — answers **400**,
   * not 401: "refreshToken is required (body field or refresh_token cookie)".
   * Treat only 401/403 as the end of a session and that 400 becomes "the API
   * would not answer", which parks every first-time visitor on a retry screen
   * instead of the login form. Found in a real browser; jsdom never sees it,
   * because a mocked refresh is whatever the test decided to return.
   *
   * So: a 429, a 5xx or a dead connection leaves the question open. Every other
   * answer is an answer.
   */
  get endsSession(): boolean {
    return !TRANSIENT.has(this.kind);
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/**
 * NestJS's ValidationPipe returns `details` as an array of sentences that each
 * begin with the offending property: `"identifier must be a string"`,
 * `"address.city should not be empty"`. That prefix is the only field
 * attribution the contract gives us, so it is what AntD's `Form` gets fed.
 *
 * A message that doesn't start with an identifier is kept as a form-level
 * error rather than guessed at — a validation message landing on the wrong
 * field is worse than one landing on none.
 */
const FIELD_PREFIX = /^([a-z][\w$]*(?:\.[\w$]+)*)\s+/;

export function toFieldErrors(details: unknown): Record<string, string[]> {
  if (!Array.isArray(details)) return {};

  const fieldErrors: Record<string, string[]> = {};
  for (const detail of details) {
    if (typeof detail !== 'string') continue;
    const field = detail.match(FIELD_PREFIX)?.[1];
    if (!field) continue;
    (fieldErrors[field] ??= []).push(detail);
  }
  return fieldErrors;
}

function parseEnvelope(body: unknown): ApiErrorEnvelope['error'] {
  if (body && typeof body === 'object' && 'error' in body) {
    const { error } = body as ApiErrorEnvelope;
    if (error && typeof error === 'object') return error;
  }
  return undefined;
}

export function toAppError(input: {
  body?: unknown;
  response?: Response;
  correlationId?: string;
  cause?: unknown;
}): AppError {
  const { body, response, correlationId, cause } = input;

  if (!response) {
    return new AppError({
      kind: 'network',
      message: cause instanceof Error ? cause.message : 'Network request failed',
      userMessage: MESSAGE_BY_KIND.network,
      cause,
    });
  }

  const status = response.status;
  const envelope = parseEnvelope(body);
  const kind = KIND_BY_STATUS[status] ?? 'server';
  const fieldErrors = kind === 'validation' || kind === 'businessRule' ? toFieldErrors(envelope?.details) : {};

  // The server's 429 body says "ThrottlerException: Too many requests" — true,
  // English, and no use to anyone in the office. What it does not say is the one
  // thing worth knowing, which is in the headers.
  const rateLimit = kind === 'rateLimited' ? readRateLimit(response) : undefined;

  return new AppError({
    kind,
    status,
    code: envelope?.code,
    // The server's own text is kept as `message` for logs and for the screens
    // that are better off relaying it — 422s from docs/04 §2.1 in particular
    // say exactly which business rule was broken.
    message: envelope?.message ?? `${status} ${response.statusText}`,
    userMessage: rateLimit
      ? rateLimitMessage(rateLimit.rateLimitScope, rateLimit.retryAfterSeconds)
      : envelope?.message && RELAY_SERVER_MESSAGE.has(kind)
        ? envelope.message
        : MESSAGE_BY_KIND[kind],
    fieldErrors,
    details: envelope?.details,
    correlationId,
    retryAfterSeconds: rateLimit?.retryAfterSeconds,
    retryAt: rateLimit?.retryAfterSeconds === undefined ? undefined : Date.now() + rateLimit.retryAfterSeconds * 1000,
    rateLimitScope: rateLimit?.rateLimitScope,
    cause,
  });
}
